// kitchen/web/src/components/TableDishRow.tsx
// Single dish row for the per-table KDS view — dense/compact layout.
// Mirrors OrderCard's item status-advance logic (itemsApi.updateStatus / bump).

import { useState } from "react";
import { ChevronRight, CheckCircle2 } from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";
import { itemsApi } from "../api/client";
import type { DelayTier } from "@shared/kds/table-board";
import DishIngredientsModal from "./DishIngredientsModal";

// ─── Shared status maps (mirrors OrderCard) ───────────────────────────────────

const STATUS_DOT_CLASS: Record<string, string> = {
  PENDING: "bg-kds-pending",
  CONFIRMED: "bg-kds-pending",
  IN_PROGRESS: "bg-kds-preparing",
  PREPARING: "bg-kds-preparing",
  READY: "bg-kds-ready",
  SERVED: "bg-kds-served",
  CANCELLED: "bg-kds-cancelled",
};

const STATUS_LABEL: Record<string, string> = {
  PENDING: "Da fare",
  CONFIRMED: "Da fare",
  IN_PROGRESS: "In prep.",
  PREPARING: "In prep.",
  READY: "Pronto",
  SERVED: "Servito",
  CANCELLED: "Annullato",
};

const STATUS_CYCLE: Record<string, string> = {
  PENDING: "PREPARING",
  PREPARING: "READY",
};

// ─── Delay tier styling (US3) ────────────────────────────────────────────────

const DELAY_TIER_CLASS: Record<DelayTier, string> = {
  normal: "",
  warn: "border-l-2 border-yellow-400 bg-yellow-400/5",
  overdue: "border-l-2 border-red-500 bg-red-500/10 animate-pulse",
};

// ─── Props ────────────────────────────────────────────────────────────────────

interface TableDishRowProps {
  item: any;
  /** US3 delay coloring. Default "normal" = no styling. */
  delayTier?: DelayTier;
  /** FR-015 oldest badge. */
  isOldest?: boolean;
  /**
   * US2: when true, renders this row in "served preview" style
   * (strikethrough + dimmed). No status-advance controls are shown.
   */
  served?: boolean;
  /** Timestamp corrente (ms) per calcolare i minuti trascorsi. */
  now?: number;
}

// ─── Timer helpers ────────────────────────────────────────────────────────────

/** Secondi interi trascorsi da un timestamp ISO (null-safe). */
function secsSince(ts: string | null | undefined, now: number): number | null {
  if (!ts) return null;
  const t = new Date(ts).getTime();
  if (!Number.isFinite(t)) return null;
  return Math.max(0, Math.floor((now - t) / 1000));
}

/** Formatta i secondi in "mm:ss" (o "h:mm:ss" oltre l'ora). */
function fmtClock(totalSecs: number | null): string {
  if (totalSecs === null) return "—";
  const h = Math.floor(totalSecs / 3600);
  const m = Math.floor((totalSecs % 3600) / 60);
  const s = totalSecs % 60;
  const pad = (n: number) => String(n).padStart(2, "0");
  return h > 0 ? `${h}:${pad(m)}:${pad(s)}` : `${m}:${pad(s)}`;
}

// ─── Component ────────────────────────────────────────────────────────────────

export default function TableDishRow({
  item,
  delayTier = "normal",
  isOldest = false,
  served = false,
  now = Date.now(),
}: TableDishRowProps) {
  const queryClient = useQueryClient();
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);

  // CONTROLLO ONDATE: un piatto è lavorabile SOLO se la sua ondata è stata
  // firata (inviata in cucina dal cameriere) → item.firedAt != null. I piatti
  // delle ondate non ancora firate restano visibili ma grigi e SENZA bottone
  // "Inizia", con label "In attesa" (mirror della vista per-postazione).
  const isFired = !!item.firedAt;
  const isPendingUnfired =
    !served &&
    !isFired &&
    (item.status === "PENDING" || item.status === "CONFIRMED");

  // Served rows never show status-advance controls (US2 FR-010).
  // Inoltre: un PENDING non firato NON è avanzabile (ondata in attesa di invio).
  const canAdvance = !served && isFired && item.status in STATUS_CYCLE;
  const dotClass = STATUS_DOT_CLASS[item.status] ?? "bg-kds-muted";
  const label = STATUS_LABEL[item.status] ?? item.status;
  // Served rows suppress delay highlight (it's a preview, not actionable).
  const delayClass = served ? "" : DELAY_TIER_CLASS[delayTier];

  async function handleAdvance(e: React.MouseEvent) {
    e.stopPropagation();
    const nextStatus = STATUS_CYCLE[item.status];
    if (!nextStatus || loading) return;

    setLoading(true);
    try {
      if (item.status === "PREPARING") {
        await itemsApi.bump(item.id);
      } else {
        await itemsApi.updateStatus(item.id, nextStatus);
      }
      queryClient.invalidateQueries({ queryKey: ["kitchen-orders"] });
      queryClient.invalidateQueries({ queryKey: ["kitchen-station-orders"] });
    } finally {
      setLoading(false);
    }
  }

  const dishName: string = item.dish?.name ?? "—";
  const variantName: string | undefined =
    item.variant?.name ?? item.variantName;
  const quantity: number = Number(item.quantity) || 1;

  // Timer (mm:ss): da chiamata ondata (firedAt), da "Inizia" (prepStartedAt),
  // da "Pronto" (completedAt). Un piatto di un'ondata NON ancora firata non
  // ha timer (null → chip nascosto): il tempo cucina parte alla chiamata,
  // mai da createdAt (fix timer ondate non chiamate 2026-06-11).
  const waitSecs = isFired ? secsSince(item.firedAt, now) : null;
  const prepSecs = secsSince(item.prepStartedAt, now);
  const readySecs = secsSince(item.completedAt, now);

  return (
    <div
      className={[
        // Leggibile da lontano: padding generoso, target touch ampi.
        "flex items-start justify-between py-2 px-3 rounded-lg",
        "bg-kds-card/50 transition-colors",
        // Served preview: strikethrough + strong dimming (US2 FR-010).
        served ? "opacity-40 line-through" : "",
        // Ondata non firata: dimming per indicare "non ancora lavorabile".
        isPendingUnfired ? "opacity-50" : "",
        !served && item.status === "PREPARING"
          ? "bg-kds-preparing/10 animate-preparing"
          : "",
        !served && item.status === "READY" ? "bg-kds-ready/10" : "",
        delayClass,
        loading ? "opacity-60" : "",
      ]
        .filter(Boolean)
        .join(" ")}
    >
      {/* Left: dish info — compact, two-line max */}
      <div className="flex-1 min-w-0 py-0.5">
        {/* Name line: quantity prefix + dish name + variant + oldest badge */}
        <div className="flex items-baseline gap-1 flex-wrap leading-snug">
          {quantity > 1 && (
            <span className="text-lg font-extrabold text-kds-text shrink-0">
              ×{quantity}
            </span>
          )}
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              setShowModal(true);
            }}
            className="text-lg font-semibold text-kds-text leading-snug line-clamp-2 text-left hover:underline cursor-pointer"
            title="Mostra ingredienti"
          >
            {dishName}
          </button>
          {variantName && (
            <span className="text-sm text-kds-muted italic shrink-0">
              ◆ {variantName}
            </span>
          )}
          {isOldest && (
            <span
              className="text-[9px] uppercase tracking-wider text-kds-priority font-bold bg-kds-priority/10 px-1 py-px rounded shrink-0"
              title="Piatto in attesa da più tempo"
            >
              ★ Più vecchio
            </span>
          )}
          {/* Feature 121 — badge Menu Fisso */}
          {item.fixedMenuAssignmentId && (
            <span
              className="text-[9px] uppercase tracking-wider text-amber-700 font-bold bg-amber-100 px-1 py-px rounded shrink-0"
              title="Incluso nel menu a prezzo fisso"
            >
              ★ Menu Fisso
            </span>
          )}
        </div>
        {/* Secondary line: modifications + notes (very compact) */}
        {item.modifications && (
          <p className="text-sm text-kds-preparing font-medium leading-tight mt-0.5">
            &#x26A0; {item.modifications}
          </p>
        )}
        {item.notes && (
          <p className="text-sm text-kds-muted leading-tight mt-0.5">
            {item.notes}
          </p>
        )}
        {/* Feature 190 — nota di correzione: piatto spostato da un'altra
            comanda. Solo testo, nessun colore (FR-005). */}
        {item.movedFromTableLabel && (
          <p className="text-sm text-kds-muted leading-tight mt-0.5">
            {"↪ spostato da "}
            {item.movedFromTableLabel}
          </p>
        )}
        {/* Timer PROMINENTI (mm:ss): attesa solo su ondate FIRATE (il
            cronometro parte alla chiamata, mai da createdAt — fix timer
            ondate non chiamate 2026-06-11) + prep/pronto secondo stato */}
        {!served && (
          <div className="flex items-center gap-2 mt-1.5">
            {waitSecs !== null && (
              <span
                className={[
                  "inline-flex items-center gap-1 px-2 py-1 rounded-md font-bold tabular-nums text-base",
                  delayTier === "overdue"
                    ? "bg-red-500/25 text-red-400"
                    : delayTier === "warn"
                      ? "bg-yellow-400/25 text-yellow-300"
                      : "bg-kds-muted/20 text-kds-text",
                ].join(" ")}
                title="Tempo dalla chiamata dell'ondata"
              >
                ⏱ {fmtClock(waitSecs)}
              </span>
            )}
            {prepSecs !== null && item.status === "PREPARING" && (
              <span
                className="inline-flex items-center gap-1 px-2 py-1 rounded-md font-bold tabular-nums text-base bg-kds-preparing/25 text-kds-preparing"
                title="Tempo da «Inizia»"
              >
                ▶ {fmtClock(prepSecs)}
              </span>
            )}
            {readySecs !== null && item.status === "READY" && (
              <span
                className="inline-flex items-center gap-1 px-2 py-1 rounded-md font-bold tabular-nums text-base bg-kds-ready/25 text-kds-ready"
                title="Tempo da «Pronto»"
              >
                ✓ {fmtClock(readySecs)}
              </span>
            )}
          </div>
        )}
      </div>

      {/* Right: status dot + advance button */}
      <div className="ml-3 flex items-center gap-2 shrink-0 pt-0.5">
        <span
          className={`inline-block w-3 h-3 rounded-full ${dotClass} shrink-0`}
          title={label}
        />

        {canAdvance && !loading && (
          <button
            type="button"
            onClick={handleAdvance}
            className={[
              "flex items-center gap-1 px-3 py-2 rounded-lg text-sm font-bold transition-colors",
              // Target touch ampio per monitor cucina.
              "min-h-[40px]",
              item.status === "PREPARING"
                ? "bg-kds-preparing text-black hover:brightness-110"
                : "bg-kds-card text-kds-text hover:bg-kds-card/70",
            ].join(" ")}
            title={
              item.status === "PREPARING"
                ? "Segna come pronto"
                : "Inizia preparazione"
            }
          >
            {item.status === "PREPARING" ? (
              <>
                <CheckCircle2 size={16} />
                Pronto
              </>
            ) : (
              <>
                <ChevronRight size={16} />
                Inizia
              </>
            )}
          </button>
        )}

        {loading && (
          <span className="w-5 h-5 border-2 border-kds-muted border-t-transparent rounded-full animate-spin" />
        )}

        {/* Ondata non ancora firata: label esplicativa, niente bottone Inizia.
            Spiega il PERCHÉ non è lavorabile: il cameriere non ha ancora
            inviato questa portata in cucina. */}
        {isPendingUnfired && (
          <span
            className="flex flex-col items-end text-[11px] text-kds-muted italic font-semibold leading-tight text-right"
            title="La portata non è ancora stata inviata in cucina dal cameriere"
          >
            <span>⏳ In attesa</span>
            <span className="text-[10px] not-italic font-normal">
              portata non inviata
            </span>
          </span>
        )}

        {item.status === "READY" && !served && (
          <span className="inline-flex items-center gap-1 text-sm text-kds-ready font-bold">
            <CheckCircle2 size={16} /> Pronto
          </span>
        )}
      </div>

      {showModal && (
        <DishIngredientsModal item={item} onClose={() => setShowModal(false)} />
      )}
    </div>
  );
}
